import axios from 'axios';
import Vue from 'vue';
import ApiError from './Errors/ApiError';
import i18n from './i18n';
import echo from './echo';

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.timeout = process.env.NODE_ENV === 'development' ? 60000 : 120000;
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

axios.defaults.headers.common['Accept-Language'] = i18n.locale;

export async function ensureCsrfCookie() {
  await axios.get('/users/csrf-cookie');
}

function isLogoutRequest(config) {
  return config?.url?.includes?.('users/logout');
}

axios.interceptors.request.use(
  (config) => {
    if (echo.socketId()) {
      config.headers['X-Socket-Id'] = echo.socketId();
    }

    return config;
  },
  (error) => Promise.reject(new ApiError(error.message, {}, 0)),
);

function isCsrfMismatch(error) {
  if (!error.response) return false;
  const isCsrfCookieRequest = error.config?.url?.includes?.('csrf-cookie');
  if (isCsrfCookieRequest) return false;
  const { status, data } = error.response;
  const message = (data?.message || '').toLowerCase();
  return (status === 419 || status === 500) && (message.includes('csrf') || message.includes('token mismatch'));
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.code === 'ECONNABORTED') {
      return Promise.reject(new ApiError(i18n.t('common.timeoutError')));
    }

    let { message } = error;
    let data = {};

    if (!error.response) {
      return Promise.reject(new ApiError(i18n.t('common.networkError'), data, null));
    }

    const config = error.config;
    if (isCsrfMismatch(error) && config && !config._csrfRetry) {
      config._csrfRetry = true;
      try {
        await ensureCsrfCookie();
        return axios.request(config);
      } catch (csrfRefreshError) {
        return Promise.reject(csrfRefreshError?.response ? error : csrfRefreshError);
      }
    }

    if (error.response.status === 401 && config && isLogoutRequest(config) && !config._logoutCsrfRetry) {
      config._logoutCsrfRetry = true;
      try {
        await ensureCsrfCookie();
        return axios.request(config);
      } catch (logoutRetryError) {
        return Promise.reject(logoutRetryError?.response ? error : logoutRetryError);
      }
    }

    if (error.response.status === 401) {
      // eslint-disable-next-line global-require
      const store = require('./store').default;
      store.dispatch('entities/deleteAll');
    }

    if (![401, 422].includes(error.response.status)) {
      Vue.prototype.$toast.error(error.response.data.message);
    }

    if (!error.response.data) {
      return Promise.reject(new ApiError(message, data, error.response.status));
    }

    if (error.response.data.message) {
      // eslint-disable-next-line prefer-destructuring
      message = error.response.data.message;
    }

    if (error.response.data.errors) {
      data = error.response.data.errors;
    }

    return Promise.reject(new ApiError(message, data, error.response.status, error.response.data));
  },
);

export default axios;
